/**
 * Kinder im Haushalt (E16) auf dem Gerät.
 *
 * Anlegen, entfernen und Geräte-Codes gehen **nur online** über
 * `/households/children` — Mitgliedschaften schreibt nur der Server. Notizen
 * über ein Kind sind gewöhnliche Sync-Zeilen (`member_notes`): lokal zuerst,
 * dann die Outbox; wer sie sieht, entscheidet der Server beim Holen.
 */

import { newId } from '@kraftwerk/shared';
import type { ChildLevel, NoteVisibility } from '@kraftwerk/shared';
import { apiFetch } from '../api.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalHouseholdMember, LocalMemberNote } from './local.js';
import { stamp, writeRow } from './write.js';

export function isChild(member: Pick<LocalHouseholdMember, 'role'>): boolean {
  return member.role === 'child';
}

export async function createChild(input: {
  name: string;
  birthYear: number | null;
}): Promise<{ userId: string; householdId: string }> {
  return apiFetch('/households/children', { method: 'POST', body: JSON.stringify(input) });
}

export async function removeChild(userId: string): Promise<void> {
  await apiFetch<undefined>(`/households/children/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
}

export async function createChildCode(
  userId: string,
): Promise<{ code: string; expiresAt: string }> {
  return apiFetch(`/households/children/${encodeURIComponent(userId)}/code`, { method: 'POST' });
}

/** Stufe eines Kindes setzen (E20) — schreibt nur der Server, wie jede Mitgliedschaft. */
export async function setChildLevel(userId: string, level: ChildLevel): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(userId)}/level`, {
    method: 'POST',
    body: JSON.stringify({ level }),
  });
}

/**
 * Ernährung für ein Teen freigeben oder entziehen (27.10.2026) — schreibt nur
 * der Server; unter Stufe `teen` antwortet er 409.
 */
export async function setTeenNutrition(userId: string, enabled: boolean): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(userId)}/nutrition`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

/**
 * Training für ein Teen freigeben oder entziehen (28.10.2026) — wie
 * `setTeenNutrition`.
 */
export async function setTeenTraining(userId: string, enabled: boolean): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(userId)}/training`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

/**
 * Wasserziel eines Kindes (28.10.2026): Das Profil des Kindes liegt nicht auf
 * dem Gerät der Eltern — gelesen und geschrieben wird beim Server.
 */
export async function childWater(userId: string): Promise<{ targetMl: number; custom: boolean }> {
  return apiFetch(`/households/children/${encodeURIComponent(userId)}/water`);
}

export async function setChildWater(userId: string, targetMl: number): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(userId)}/water`, {
    method: 'POST',
    body: JSON.stringify({ targetMl }),
  });
}

/** Notizen über ein Kind — neueste zuerst. Was hier liegt, durfte ich holen. */
export async function notesAbout(
  childId: string,
  database: KraftwerkDatabase = db,
): Promise<LocalMemberNote[]> {
  return (await database.member_notes.where('aboutUserId').equals(childId).toArray())
    .filter((note) => !note.deletedAt)
    .sort((a, b) => b.date.localeCompare(a.date) || b.updatedAt.localeCompare(a.updatedAt));
}

export async function saveMemberNote(
  input: {
    householdId: string;
    aboutUserId: string;
    visibility: NoteVisibility;
    date: string;
    body: string;
  },
  existing: LocalMemberNote | null = null,
  database: KraftwerkDatabase = db,
): Promise<LocalMemberNote> {
  return writeRow(
    'member_notes',
    {
      ...existing,
      id: existing?.id ?? newId(),
      ...input,
      body: input.body.trim(),
      updatedAt: stamp(),
      deletedAt: null,
    },
    'upsert',
    database,
  );
}

export async function deleteMemberNote(
  note: LocalMemberNote,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  await writeRow('member_notes', { ...note, updatedAt: now, deletedAt: now }, 'delete', database);
}
